import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import type { PublicPickupView } from '@azko/shared';
import { ApiRequestError, api } from '../lib/api.js';
import { formatReceived, statusLabel, statusTone } from '../lib/format.js';
import { Card, MadeByAzkoGroup, Spinner, StatusBadge } from '../components/ui.js';
import { IconCheck, IconPackage } from '../components/icons.js';

/**
 * A pagina que o morador abre pelo link do WhatsApp.
 *
 * E a unica tela do produto vista por quem nao tem conta — e, na pratica, o
 * primeiro contato de um morador com a Azko. Por isso nao pede login, nao pede
 * condominio e nao usa o AppShell: nada aqui pressupoe sessao.
 *
 * Desenhada para ser lida de pe na fila da portaria, no celular, com uma mao
 * so: o QR e o codigo curto sao os unicos elementos grandes, e nao ha nada para
 * tocar. Ler basta.
 */
export function PickupPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [pickup, setPickup] = useState<PublicPickupView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api
      .get<PublicPickupView>(`/pickups/${encodeURIComponent(token)}`)
      .then((data) => {
        if (cancelled) return;
        setPickup(data);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setPickup(null);
        // 404 e 422 dizem a mesma coisa para quem esta lendo: o link nao serve.
        // Link truncado pelo aplicativo de mensagem cai no 422, e isso nao pode
        // virar "algo deu errado" — o morador tentaria de novo para sempre.
        setError(
          cause instanceof ApiRequestError && cause.status < 500
            ? 'Este link de retirada nao e valido. Peca um novo na portaria.'
            : 'Nao conseguimos abrir sua encomenda agora. Tente de novo em instantes.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <PickupShell>
        <div className="grid place-items-center gap-3 py-16 text-muted">
          <Spinner className="size-6" />
          <p className="text-sm">Abrindo sua encomenda...</p>
        </div>
      </PickupShell>
    );
  }

  if (error || !pickup) {
    return (
      <PickupShell>
        <Card className="p-6 text-center">
          <p className="eyebrow">Link de retirada</p>
          <p className="mt-3 font-semibold">{error}</p>
        </Card>
      </PickupShell>
    );
  }

  // "101" sozinho nao localiza nada onde todo bloco tem um 101 — e o morador
  // que recebe este link e justamente quem precisa saber em qual predio esta.
  const local = pickup.block ? `${pickup.block} ${pickup.unitLabel}` : pickup.unitLabel;

  // Retirada parcial: ja levou parte, ainda ha o que buscar. E o unico estado
  // em que o `status` desta pagina ("aguardando") nao conta a historia toda —
  // sem dizer o numero, o morador desce achando que vai buscar o carrinho
  // inteiro e volta sem entender o que aconteceu com o resto.
  const parcial = pickup.boxesAwaiting > 0 && pickup.boxesAwaiting < pickup.boxCount;

  return (
    <PickupShell>
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between gap-4 border-b border-line p-5">
          <div className="flex items-center gap-3">
            <span className="grid size-10 place-items-center rounded-xl bg-surface-2 text-accent">
              <IconPackage />
            </span>
            <div>
              <p className="eyebrow">{pickup.boxCount > 1 ? 'Encomendas' : 'Encomenda'}</p>
              <p className="text-lg font-bold tracking-tight">#{pickup.code}</p>
            </div>
          </div>
          <StatusBadge tone={statusTone(pickup.status)}>{statusLabel(pickup.status)}</StatusBadge>
        </div>

        {pickup.status === 'awaiting' ? (
          <div className="flex flex-col items-center gap-5 p-6 text-center">
            <PickupQr />
            <div>
              {/* O numero de volumes vem ANTES do QR de proposito: e o que faz o
                  morador descer com as duas maos livres, e o que ele confere no
                  balcao antes de assinar. Um QR so para tres caixas e o ganho do
                  lote; nao dizer que sao tres transformaria esse ganho num
                  problema do porteiro. */}
              {pickup.boxCount > 1 && (
                <p className="mb-4 text-lg font-bold">
                  {parcial
                    ? `Faltam ${pickup.boxesAwaiting} de ${pickup.boxCount} volumes`
                    : `${pickup.boxCount} volumes para retirar`}
                </p>
              )}
              <p className="text-sm text-muted">
                {pickup.boxCount > 1
                  ? 'Mostre este QR na portaria — ele vale por todos'
                  : 'Mostre este QR na portaria'}
              </p>
              {/* O QR resolve o caso normal; o codigo curto existe para quando a
                  tela nao le — sol batendo, brilho no minimo, bateria no fim.
                  Ditavel por telefone, de proposito. */}
              <p className="mt-4 text-xs text-faint">Se a camera nao ler, informe o codigo</p>
              <p className="text-3xl font-bold tracking-[0.2em]">{pickup.code}</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 p-6 text-center">
            <span className="grid size-12 place-items-center rounded-full bg-surface-2 text-muted">
              <IconCheck />
            </span>
            <p className="font-semibold">
              {pickup.pickedUpAt
                ? `${pickup.boxCount > 1 ? `${pickup.boxCount} volumes retirados` : 'Retirada'} em ${formatReceived(pickup.pickedUpAt)}`
                : 'Esta encomenda nao esta mais disponivel para retirada.'}
            </p>
            {/* Nao dizemos QUEM retirou. O link circula em grupo de familia e de
                condominio; quem pegou o pacote e dado do morador, nao de quem
                tem o link. Na portaria, autenticado, isso aparece. */}
            <p className="text-sm text-muted">
              Nao foi voce? Procure a portaria com o codigo #{pickup.code}.
            </p>
          </div>
        )}

        <dl className="grid grid-cols-2 gap-px border-t border-line bg-line text-sm">
          <Detail label="Destinatario" value={pickup.residentName ?? 'Nao informado'} />
          <Detail label="Unidade" value={local} />
          <Detail label="Transportadora" value={pickup.carrier || 'Nao informada'} />
          <Detail label="Recebida" value={formatReceived(pickup.receivedAt)} />
          {pickup.boxCount > 1 && (
            <Detail
              label="Volumes"
              value={parcial ? `${pickup.boxesAwaiting} de ${pickup.boxCount} na portaria` : String(pickup.boxCount)}
            />
          )}
        </dl>
      </Card>
    </PickupShell>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 bg-surface p-4">
      <dt className="eyebrow">{label}</dt>
      {/* Duas colunas num celular de 360px dao ~170px por celula: nome composto
          de morador ou transportadora com nome longo estouravam a caixa. */}
      <dd className="mt-1 break-words font-semibold">{value}</dd>
    </div>
  );
}

function PickupShell({ children }: { children: ReactNode }) {
  return (
    <div className="safe-area-all safe-screen px-4 py-10">
      <main className="mx-auto w-full max-w-md">
        {children}
        <MadeByAzkoGroup className="mt-8 text-center" />
      </main>
    </div>
  );
}

/**
 * O mesmo QR que a portaria ve no registro, redesenhado no celular do morador a
 * partir da URL desta propria pagina.
 *
 * A URL da barra de enderecos **e** o conteudo do QR: e assim que o leitor da
 * portaria sabe ler (`extractPickupToken` tira o token do fim do caminho). Ler
 * de `window.location` em vez de pedir o token de volta a API mantem o segredo
 * num lugar so — o corpo do JSON nao precisa carrega-lo.
 */
function PickupQr() {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  // Sem query nem hash: o leitor pega o ultimo segmento do caminho, e um
  // `?utm_source=` grudado pelo aplicativo de mensagem entraria no QR.
  const url = `${window.location.origin}${window.location.pathname}`;

  useEffect(() => {
    let cancelled = false;

    void import('qrcode').then(async (QRCode) => {
      const image = await QRCode.toDataURL(url, {
        margin: 1,
        width: 320,
        errorCorrectionLevel: 'M',
        // Fundo branco fixo, mesmo em tema escuro: leitor de celular erra com
        // QR invertido, e este e o unico QR que ninguem pode reimprimir.
        color: { dark: '#000000', light: '#ffffff' },
      });
      if (!cancelled) setDataUrl(image);
    });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!dataUrl) return <div className="size-56 animate-pulse rounded-2xl bg-surface-2" />;

  return (
    <img
      src={dataUrl}
      alt="QR Code de retirada da encomenda"
      className="size-56 rounded-2xl bg-white p-3"
    />
  );
}
